import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Button, Space, Spin, message } from 'antd';
import { GiftOutlined, ArrowLeftOutlined, ClockCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, Discount } from '../utils/api';

const { Title, Text } = Typography;

const DiscountPage: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(true);
  const [usingDiscount, setUsingDiscount] = useState<number | null>(null);

  // 检查用户是否已登录
  useEffect(() => {
    if (!authState.isLoading && !authState.isAuthenticated) {
      navigate('/login');
    }
  }, [authState.isLoading, authState.isAuthenticated, navigate]);

  // 加载优惠券列表
  useEffect(() => {
    const loadDiscounts = async () => {
      if (!authState.token) {
        return;
      }

      try {
        setLoading(true);
        const response = await api.getDiscount(authState.token);
        
        if (response.status === 200 && response.data) {
          setDiscounts(response.data.discounts || []);
        } else {
          message.error(response.msg || '获取优惠券列表失败');
        }
      } catch (error) {
        console.error('获取优惠券失败:', error);
        message.error('获取优惠券列表失败');
      } finally {
        setLoading(false);
      }
    };

    if (authState.isAuthenticated && authState.token) {
      loadDiscounts();
    }
  }, [authState.isAuthenticated, authState.token]);

  // 使用优惠券
  const handleUseDiscount = async (discountId: number) => {
    // 使用浏览器原生确认框
    const confirmed = window.confirm('您确定要使用这张优惠券吗？使用后将无法撤销。');
    
    if (!confirmed) {
      return;
    }

    if (!authState.token) {
      message.error('请先登录');
      return;
    }

    try {
      setUsingDiscount(discountId);
      const response = await api.useDiscount(
        { discount_id: discountId },
        authState.token
      );

      if (response.status === 200) {
        message.success('优惠券使用成功！');
        // 重新加载优惠券列表
        const reloadResponse = await api.getDiscount(authState.token);
        if (reloadResponse.status === 200 && reloadResponse.data) {
          setDiscounts(reloadResponse.data.discounts || []);
        }
      } else {
        message.error(response.msg || '使用优惠券失败');
      }
    } catch (error) {
      console.error('使用优惠券失败:', error);
      message.error('使用优惠券失败');
    } finally {
      setUsingDiscount(null);
    }
  };

  // 返回主页
  const handleBack = () => {
    navigate('/');
  };

  // 格式化来源类型
  const formatSourceType = (sourceType: number, sourceDetail: string) => {
    switch (sourceType) {
      case 1:
        return `首次使用 - ${sourceDetail}`;
      case 2:
        return `邀请用户 - ${sourceDetail}`;
      case 3:
        return `提出意见 - ${sourceDetail}`;
      default:
        return sourceDetail;
    }
  };

  // 格式化时间
  const formatTime = (timeString: string) => {
    if (!timeString) return '-';
    const date = new Date(timeString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 计算未使用的优惠券数量
  const unusedCount = discounts.filter(d => !d.Used).length;

  // 判断某张优惠券是否可以使用（前2张未使用的券可用）
  const canUseDiscount = (discountId: number) => {
    const unusedDiscounts = discounts
      .filter(d => !d.Used)
      .sort((a, b) => new Date(a.CreatedAt).getTime() - new Date(b.CreatedAt).getTime());
    
    const index = unusedDiscounts.findIndex(d => d.ID === discountId);
    return index >= 0 && index < 2; // 前2张（索引0和1）可以使用
  };

  // 如果正在加载
  if (authState.isLoading || loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // 如果未登录
  if (!authState.isAuthenticated) {
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* 顶部导航栏 */}
      <div style={{
        width: '100%',
        maxWidth: '1200px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '40px'
      }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleBack}
          size="large"
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white'
          }}
        >
          返回主页
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <GiftOutlined style={{ fontSize: '32px', color: 'white' }} />
          <Title level={2} style={{ color: 'white', margin: 0 }}>
            我的优惠券
          </Title>
        </div>
        <div style={{ width: '100px' }} /> {/* 占位符保持居中 */}
      </div>

      {/* 优惠券统计 */}
      <div style={{
        width: '100%',
        maxWidth: '1200px',
        marginBottom: '40px',
        textAlign: 'center'
      }}>
        <Text style={{ color: 'white', fontSize: '18px' }}>
          共有 {discounts.length} 张优惠券，其中 {unusedCount} 张未使用
        </Text>
      </div>

      {/* 优惠券列表 */}
      <Row gutter={[24, 24]} style={{ width: '100%', maxWidth: '1200px' }}>
        {discounts.length === 0 ? (
          <Col span={24}>
            <Card style={{
              borderRadius: '16px',
              textAlign: 'center',
              padding: '40px'
            }}>
              <GiftOutlined style={{ fontSize: '64px', color: '#ccc', marginBottom: '16px' }} />
              <Title level={4} style={{ color: '#999' }}>暂无优惠券</Title>
              <Text style={{ color: '#999' }}>
                您还没有优惠券，快去参与活动获取吧！
              </Text>
            </Card>
          </Col>
        ) : (
          discounts.map((discount) => (
            <Col xs={24} sm={24} md={24} lg={24} key={discount.ID}>
              <Card
                style={{
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  border: discount.Used ? '2px solid #d9d9d9' : '2px solid #1890ff',
                  height: '100%',
                  background: discount.Used ? '#f5f5f5' : '#ffffff',
                }}
                bodyStyle={{
                  padding: '24px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {/* 优惠券头部 */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <GiftOutlined style={{
                    fontSize: '32px',
                    color: discount.Used ? '#999' : '#1890ff',
                    marginRight: '12px'
                  }} />
                  <Title level={4} style={{
                    color: discount.Used ? '#999' : '#000',
                    margin: 0,
                    flex: 1
                  }}>
                    咖啡厅7折优惠券
                  </Title>
                  {discount.Used && (
                    <CheckCircleOutlined style={{
                      fontSize: '24px',
                      color: '#999'
                    }} />
                  )}
                </div>

                {/* 优惠券信息 */}
                <Space direction="vertical" size="small" style={{ width: '100%', flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: discount.Used ? '#999' : '#000'
                  }}>
                    <ClockCircleOutlined style={{ marginRight: '8px' }} />
                    <Text style={{ color: discount.Used ? '#999' : '#000' }}>
                      获取时间：{formatTime(discount.CreatedAt)}
                    </Text>
                  </div>
                  
                  <div style={{
                    padding: '12px',
                    background: discount.Used ? '#e8e8e8' : '#f0f5ff',
                    borderRadius: '8px',
                    marginTop: '8px'
                  }}>
                    <Text style={{
                      color: discount.Used ? '#999' : '#000',
                      fontSize: '14px',
                      display: 'block',
                      marginBottom: '4px',
                      fontWeight: 'bold'
                    }}>
                      来源
                    </Text>
                    <Text style={{
                      color: discount.Used ? '#999' : '#595959',
                      fontSize: '14px'
                    }}>
                      {formatSourceType(discount.SourceType, discount.SourceDetail)}
                    </Text>
                  </div>
                </Space>

                {/* 优惠券底部操作区 */}
                <div style={{ marginTop: '16px' }}>
                  {discount.Used ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '12px',
                      background: '#e8e8e8',
                      borderRadius: '8px'
                    }}>
                      <Text style={{ color: '#999', fontWeight: 'bold' }}>
                        已使用
                      </Text>
                      <br />
                      <Text style={{ color: '#999', fontSize: '12px' }}>
                        使用时间：{formatTime(discount.UsedAt)}
                      </Text>
                    </div>
                  ) : !canUseDiscount(discount.ID) ? (
                    <Button
                      block
                      disabled
                      size="large"
                      style={{
                        borderRadius: '8px',
                        height: '48px',
                        fontSize: '14px'
                      }}
                    >
                      本阶段超出2张优惠券暂不可用
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      block
                      size="large"
                      loading={usingDiscount === discount.ID}
                      onClick={() => handleUseDiscount(discount.ID)}
                      style={{
                        borderRadius: '8px',
                        height: '48px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        background: '#1890ff',
                        borderColor: '#1890ff'
                      }}
                    >
                      使用优惠券
                    </Button>
                  )}
                </div>
              </Card>
            </Col>
          ))
        )}
      </Row>

      {/* 底部说明 */}
      <div style={{ 
        marginTop: '60px', 
        maxWidth: '800px',
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <Title level={4} style={{ color: '#1890ff', marginBottom: '20px' }}>
          活动说明
        </Title>
        <div style={{ textAlign: 'left', lineHeight: '1.8' }}>
          <Text style={{ color: '#262626', fontSize: '15px', display: 'block', marginBottom: '16px' }}>
            <strong>即日~2026.1.31</strong>登录使用AI医疗辅助系统，即获赠7折咖啡优惠券：
          </Text>
          <div style={{ paddingLeft: '20px', marginBottom: '16px' }}>
            <Text style={{ color: '#595959', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
              ① 登录注册并完整使用AI预问诊功能可领一张；
            </Text>
            <Text style={{ color: '#595959', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
              ② 推荐员工成功注册登录可领取一张；
            </Text>
            <Text style={{ color: '#595959', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
              ③ 使用后对系统提出意见被采纳可领一张；
            </Text>
          </div>
          <Text style={{ color: '#262626', fontSize: '14px', display: 'block', lineHeight: '1.8' }}>
            每人最大可领2张，一张优惠券可对应购买1杯现磨咖啡，到咖啡馆出示成功参与资格并点击核销界面即可优惠结算。
            <span style={{ color: '#8c8c8c' }}>（优惠券消费结束时间见各厂区咖啡馆）</span>
          </Text>
        </div>
      </div>
    </div>
  );
};

export default DiscountPage;

